import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type { SessionContext } from "@thalamos/haven-common";
import {
  membershipLabel,
  sessionContextKey,
  sessionContextsFromMemberships,
} from "@thalamos/haven-common";

import { useUser } from "../../Context/UserContext.js";
import {
  useSwitchSessionContext,
  type SessionContextSwitchOutcome,
  type SessionContextSwitchProblem,
} from "../../Context/useSwitchSessionContext.js";
import { consumeReturnTo } from "../../routing/returnTo.js";

export const SessionContextSelectorPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { search } = useLocation();
  const switchTo = useSwitchSessionContext();

  const choose = async (
    context: SessionContext,
  ): Promise<SessionContextSwitchOutcome> => {
    const outcome = await switchTo(context);

    if (outcome === "accepted") {
      navigate(consumeReturnTo(search), { replace: true });
    }

    return outcome;
  };

  return (
    <SessionContextSelectorPageContent
      contexts={sessionContextsFromMemberships(user?.memberships ?? [])}
      onChoose={choose}
    />
  );
};

/**
 * The picker itself, with the contexts to offer and the act of choosing one
 * injected rather than reached for through context and the API client. Mounting
 * this needs neither a session nor a router, so a story can show any state the
 * page has — including a refusal — without standing up either.
 */
export const SessionContextSelectorPageContent = ({
  contexts,
  onChoose,
}: {
  contexts: SessionContext[];
  onChoose: (
    context: SessionContext,
  ) => Promisable<SessionContextSwitchOutcome>;
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  );
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<SessionContextSwitchProblem | null>(
    null,
  );

  // Selection is held as a position, not as an organisation id: a user may do
  // two kinds of work at the same organisation, so an id identifies a row's
  // organisation but not the row.
  const selected =
    selectedIndex === undefined ? undefined : contexts[selectedIndex];

  const choose = async (context: SessionContext) => {
    setSubmitting(true);
    setProblem(null);

    try {
      const outcome = await onChoose(context);
      setProblem(outcome === "accepted" ? null : outcome);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, width: "100%" }}>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h5" component="h1">
              {t("sessionContextSelector.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("sessionContextSelector.description")}
            </Typography>
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="session-context-selector-label">
              {t("sessionContextSelector.label")}
            </InputLabel>
            <Select
              labelId="session-context-selector-label"
              label={t("sessionContextSelector.label")}
              // A controlled Select needs a string for "nothing chosen"; giving
              // it `undefined` makes it uncontrolled instead.
              value={selectedIndex === undefined ? "" : String(selectedIndex)}
              onChange={(event) => {
                setSelectedIndex(Number(event.target.value));
              }}
            >
              {contexts.map((context, index) => (
                <MenuItem
                  key={sessionContextKey(context)}
                  value={String(index)}
                >
                  {/* The kind's name, not the value stored for it: nobody
                      chooses a context called `organisation_administration`. */}
                  {`${membershipLabel(context)} — ${t(`sessionContextKinds.${context.kind}`)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {problem === null ? null : (
            <Alert severity="error">
              {match(problem)
                .with("refused", () => t("errors.sessionContextFailed"))
                .with("failed", () => t("errors.sessionContextUnavailable"))
                .exhaustive()}
            </Alert>
          )}

          <Button
            variant="contained"
            disabled={selected === undefined || submitting}
            onClick={() => {
              if (selected) {
                void choose(selected);
              }
            }}
          >
            {t("sessionContextSelector.continue")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
